import { SnakeFood } from './SnakeFood.js';
import { LevelCompleteOverlay } from '../../ui/LevelCompleteOverlay.js';
import { GameOverOverlay, STAT_ICONS } from '../../ui/GameOverOverlay.js';
import { ScoreSubmitOverlay } from '../../ui/ScoreSubmitOverlay.js';
import { partyMode } from '../../ui/Confetti.js';
import { drawHouse } from './SnakeMode_Adventure.js';
import { t } from '../../config/i18n.js';
import { SNAKE_CONFIG } from './snakeConfig.js';

const {
    ADVENTURE_HOUSE_W, ADVENTURE_HOUSE_H,
    FREEROAM_FOOD_COUNT,
} = SNAKE_CONFIG;

/**
 * Free roam mode — picks a far-off house position once the player crosses the
 * score threshold. Reveals the house on the map.
 */
export function placeHouse(scene) {
    const { cols, rows } = scene._grid;
    const excluded = scene._buildExcludedSet();
    const margin = 8;
    const maxAttempts = 300;
    const hw = ADVENTURE_HOUSE_W;
    const hh = ADVENTURE_HOUSE_H;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const col = margin + Math.floor(Math.random() * (cols - hw - margin * 2));
        const row = margin + Math.floor(Math.random() * (rows - hh - margin * 2));
        let blocked = false;
        for (let dc = -1; dc <= hw; dc++) {
            for (let dr = -1; dr <= hh; dr++) {
                if (excluded.has(`${col + dc},${row + dr}`)) { blocked = true; break; }
            }
            if (blocked) break;
        }
        if (!blocked) {
            scene._housePos = { col, row };
            break;
        }
    }
    if (!scene._housePos) {
        scene._housePos = { col: Math.floor(cols / 2) + 10, row: Math.floor(rows / 2) + 10 };
    }

    scene._freeRoamHouseRevealed = true;
    drawHouse(scene);
}

/**
 * Spawns the extra food pellets that exist alongside the primary food in free
 * roam (so the player isn't searching the whole map for one pickup).
 */
export function spawnExtraFoods(scene) {
    const { cols, rows } = scene._grid;
    scene._extraFoods = [];
    for (let i = 0; i < FREEROAM_FOOD_COUNT - 1; i++) {
        const f = new SnakeFood(scene.game.theme);
        f.spawn(cols, rows, scene._snake, scene._obstacles, scene._perks);
        scene._world.addChild(f.graphic);
        scene._extraFoods.push(f);
    }
}

export function showWin(scene) {
    scene._endGame();

    const timeLeft = Math.ceil(scene._freeRoamTimer);

    partyMode();
    // Shared cross-game victory jingle on the winner screen (ducks the
    // looping ambience so it plays clean).
    scene._audio?.playFinished();

    scene._winOverlay = new LevelCompleteOverlay();
    scene._winOverlay.show(scene.game.width, scene.game.height, {
        title: t('snake.freeroam_win'),
        lines: [
            t('snake.win_score', { v: scene._score }),
            t('snake.win_food', { v: scene._foodEaten }),
            t('snake.win_time', { v: timeLeft }),
        ],
        score: scene._score,
        scoreLabel: t('common.total_score'),
        onContinue: () => {
            if (scene._winOverlay) { scene._winOverlay.destroy(); scene._winOverlay = null; }
            scene._scoreOverlay = new ScoreSubmitOverlay();
            scene._scoreOverlay.show({
                game: 'snake',
                score: scene._score,
                outcome: 'win',
                meta: { mode: 'freeroam', foodEaten: scene._foodEaten, timeLeft },
                theme: scene.game.theme,
                onDone: () => {
                    scene._scoreOverlay = null;
                    scene.game.sceneManager.switchScene('menu');
                },
            });
        },
    });
    scene.container.addChild(scene._winOverlay.container);
}

export function showTimeout(scene) {
    scene._endGame();

    // Reuse the top-bar chip icons so the result panel matches what the
    // player saw during the run. Inline SVG fallback keeps the overlay
    // populated if the theme asset failed to load.
    const theme = scene.game?.theme;
    const cupIcon  = theme?.get('snake.hud.score') || STAT_ICONS.trophy;
    const tramIcon = theme?.get('snake.hud.cars')  || STAT_ICONS.tram;

    scene._gameOverOverlay?.destroy();
    scene._gameOverOverlay = new GameOverOverlay();
    scene._gameOverOverlay.show(scene.game.width, scene.game.height, {
        title: t('snake.freeroam_timeout'),
        stats: [
            { icon: cupIcon,  label: t('common.stat_score'), value: scene._score },
            // Food count — re-uses the SCORE label slot would be confusing,
            // so use the per-game "cars" label as a stand-in for "items".
            { icon: tramIcon, label: t('common.stat_cars'),  value: scene._foodEaten },
        ],
        actions: [
            {
                kind: 'home',
                label: t('common.main_menu'),
                onClick: () => scene.game.sceneManager.switchScene('menu'),
            },
            {
                kind: 'retry',
                label: t('common.retry'),
                primary: true,
                onClick: () => scene._showScoreOverlay(),
            },
        ],
    });
    scene.container.addChild(scene._gameOverOverlay.container);
}
